import React, { useRef, useEffect } from 'react';

interface DoubleScrollContainerProps {
    children: React.ReactNode;
    className?: string;
}

export default function DoubleScrollContainer({ children, className = '' }: DoubleScrollContainerProps) {
    const topScrollRef = useRef<HTMLDivElement>(null);
    const bottomScrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const topDiv = topScrollRef.current;
        const bottomDiv = bottomScrollRef.current;
        const contentDiv = contentRef.current;

        if (!topDiv || !bottomDiv || !contentDiv) return;

        let isSyncingTop = false;
        let isSyncingBottom = false;

        const onTopScroll = () => {
            if (isSyncingBottom) {
                isSyncingBottom = false;
                return;
            }
            isSyncingTop = true;
            bottomDiv.scrollLeft = topDiv.scrollLeft;
        };

        const onBottomScroll = () => {
            if (isSyncingTop) {
                isSyncingTop = false;
                return;
            }
            isSyncingBottom = true;
            topDiv.scrollLeft = bottomDiv.scrollLeft;
        };

        const updateWidth = () => {
            const scrollWidth = contentDiv.scrollWidth;
            const clientWidth = contentDiv.clientWidth;
            
            const dummy = topDiv.firstElementChild as HTMLElement;
            if (dummy) {
                dummy.style.width = `${scrollWidth}px`;
            }
            
            // If the content doesn't overflow, hide the top scrollbar container
            if (scrollWidth <= clientWidth) {
                topDiv.style.display = 'none';
            } else {
                topDiv.style.display = 'block';
            }
        };

        topDiv.addEventListener('scroll', onTopScroll);
        bottomDiv.addEventListener('scroll', onBottomScroll);
        
        updateWidth();
        
        const observer = new ResizeObserver(updateWidth);
        observer.observe(contentDiv);

        return () => {
            topDiv.removeEventListener('scroll', onTopScroll);
            bottomDiv.removeEventListener('scroll', onBottomScroll);
            observer.disconnect();
        };
    }, [children]);

    return (
        <div className={`double-scroll-container ${className}`}>
            {/* Top scrollbar */}
            <div 
                ref={topScrollRef} 
                className="overflow-x-auto overflow-y-hidden"
                style={{ width: '100%' }}
            >
                <div style={{ height: '1px', minHeight: '1px' }}></div>
            </div>
            {/* Bottom scrollable container */}
            <div 
                ref={bottomScrollRef} 
                className="overflow-x-auto"
                style={{ width: '100%' }}
            >
                <div ref={contentRef} className="w-full">
                    {children}
                </div>
            </div>
        </div>
    );
}
